<template>
  <panel-layout
    id="panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #heading>
      <div v-if="customerCourse">
        <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85 mb-1">
          {{ customerCourse.customer.displayName }}
        </h2>
        <p class="text-sm md:text-base opacity-80">
          {{ customerCourse.course.name }} - {{ customerCourse.courseRun.information }}
        </p>
      </div>
    </template>

    <template #actions-right>
      <div v-if="customerCourse" class="flex items-center space-x-1">
        <o-history-link
          :item="customerCourse"
          model="CustomerCourse"
          icon="history"
          slim
          flat
        />

        <o-tooltip class="h-8" :text="$t('pages.customer.course.id.move')">
          <o-button
            size="slim"
            flat
            icon="reservationMove"
            @click="handleMove"
          />
        </o-tooltip>

        <o-tooltip class="h-8" :text="$t('pages.customer.course.id.cancel')">
          <o-button
            size="slim"
            flat
            icon="cancel"
            @click="handleCancel"
          />
        </o-tooltip>

        <o-tooltip class="h-8" :text="$t('pages.customer.course.id.deleteTooltip')">
          <o-button
            size="slim"
            flat
            icon="delete"
            @click="handleDelete"
          />
        </o-tooltip>
      </div>
    </template>

    <div v-if="customerCourse" class="h-full">
      <o-scroll class="px-5">
        <dl class="py-4 md:py-6">
          <o-description-details
            :term="$t('general.customer')"
            class="pb-0"
          >
            <customer-select v-model="customerCourse.customer" preview create default-status-class="bg-white h-10 dark:bg-gray-900" />
          </o-description-details>

          <o-description-details
            :term="$t('general.price')"
            class="pb-0"
          >
            <currency-field v-model="customerCourse.price" />
          </o-description-details>

          <o-description-details
            :term="$t('general.status')"
            class="pb-0"
          >
            <o-chip
              v-if="customerCourse.payment?.schemeName"
              size="sm"
              flat
              variant="primary"
              class="mr-1"
            >
              {{ customerCourse.payment.schemeName }}
            </o-chip>

            <o-chip
              v-if="customerCourse.payment?.isEarly"
              size="sm"
              flat
              variant="info"
              class="mr-1"
            >
              {{ $t('general.earlyBird') }}
            </o-chip>

            <o-chip
              v-if="customerCourse.status === API.CustomerCourseStatus.confirmed"
              size="sm"
              flat
              variant="success"
            >
              {{ $t('pages.customer.course.status.confirmed') }}
            </o-chip>

            <o-chip
              v-if="customerCourse.status === API.CustomerCourseStatus.cancelled"
              size="sm"
              flat
              variant="danger"
            >
              {{ $t('pages.customer.course.status.cancelled') }}
            </o-chip>
          </o-description-details>

          <o-description-details
            :term="$t('pages.customer.course.id.payment')"
          >
            <div v-if="customerCourse">
              <o-panel-link
                v-if="customerCourse.order"
                :path="`/order/${customerCourse.order.idOrder}`"
                class="block p-2 border rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 dark:bg-gray-900 dark:text-gray-200"
              >
                <div class="flex justify-between items-center flex-grow leading-none">
                  <div class="leading-none">
                    <p class="mb-1">
                      #{{ customerCourse.order.orderNumber }}
                    </p>
                    <p class="text-xs text-gray-600 dark:text-gray-300">
                      {{ customerCourse.order.dateLabel }}
                    </p>
                  </div>

                  <div class="flex items-center">
                    <div class="flex items-center space-x-2">
                      <o-chip v-if="customerCourse.payment?.isEarly" variant="info" flat>
                        {{ $t('general.earlyBird') }}
                      </o-chip>
                      <o-chip v-if="customerCourse.isPaid" variant="success" flat>
                        {{ $t('pages.customer.course.id.paid') }}
                      </o-chip>
                      <o-chip :variant="orderChipVariant" flat>
                        {{ customerCourse.order.statusLabel || '-' }}
                      </o-chip>
                    </div>
                    <p class="ml-4 text-sm font-semibold">
                      {{ customerCourse.order.priceLabel }}
                    </p>
                  </div>
                </div>
              </o-panel-link>

              <div v-if="!customerCourse.order && !customerCourse.isPaid">
                <o-button icon="sale" flat @click="handleCreateOrder">
                  {{ $t('pages.customer.course.id.newSale') }}
                </o-button>
              </div>
            </div>
          </o-description-details>

          <div class="mt-10">
            <h4 class="mb-3 font-medium text-gray-700 dark:text-gray-200">
              {{ $t('pages.customer.course.id.sessions') }}
            </h4>

            <div class="border rounded dark:border-gray-800">
              <div v-for="(reservation, index) in customerCourse.reservations" :key="index" class="flex items-center justify-between border-b dark:border-gray-800 last:border-none p-3">
                <div>
                  <p class="text-sm text-gray-700 dark:text-gray-200">
                    {{ reservation.reservation.dateLabel }}
                  </p>
                  <p class="text-xs text-gray-600 dark:text-gray-300">
                    {{ reservation.reservation._servicesLabel }}
                  </p>
                </div>

                <div class="flex items-center space-x-2">
                  <o-chip v-if="reservation.reservationCustomer?.isStatusCancelled" variant="danger" flat>
                    {{ $t('general.cancelled') }}
                  </o-chip>

                  <template v-if="reservation.reservationCustomer">
                    <o-chip v-if="reservation.reservationCustomer.makeupDetails?.isMakeup" variant="info" flat>
                      {{ $t('pages.customer.course.id.makeup') }}
                    </o-chip>

                    <template v-if="reservation.reservation && reservation.reservation.finishTimeUTC < locationTime">
                      <o-chip v-if="reservation.reservationCustomer.checkedIn" variant="success" flat>
                        {{ $t('reservation.checkedIn') }}
                      </o-chip>
                      <o-chip v-else-if="reservation.reservationCustomer.noShow" variant="danger" flat>
                        {{ $t('reservation.noShow') }}
                      </o-chip>
                    </template>

                    <o-panel-link
                      v-if="reservation.reservationCustomer.makeupDetails?.makeupScheduled"
                      :path="`/appointment/attendee/${reservation.reservationCustomer.makeupDetails.scheduledReservationCustomer?.idReservationCustomer}`"
                      @click.stop
                    >
                      <o-chip variant="info" flat>
                        {{ $t('pages.customer.course.id.makeupOn', { date: $date.convert(reservation.reservationCustomer.makeupDetails.scheduledReservationCustomer?.reservation?.date, undefined, $date.presets.short) }) }}
                      </o-chip>
                    </o-panel-link>

                    <o-button
                      v-else-if="reservation.reservationCustomer.makeupDetails?.makeupAvailable"
                      size="sm"
                      @click="handleBookMakeup(reservation.reservationCustomer.idReservationCustomer)"
                    >
                      {{ $t('pages.customer.course.id.scheduleMakeup') }}
                    </o-button>
                  </template>

                  <o-panel-link
                    v-if="reservation.reservationCustomer"
                    tag="o-button"
                    size="sm"
                    flat
                    icon="view"
                    :path="`/appointment/attendee/${reservation.reservationCustomer.idReservationCustomer}`"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="customerCourse.previousReservations && customerCourse.previousReservations.length" class="mt-8">
            <h4 class="mb-1 font-medium text-gray-700 dark:text-gray-200">
              {{ $t('pages.customer.course.id.previousSessions') }}
            </h4>
            <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
              {{ $t('pages.customer.course.id.previousSessionsInfo') }}
            </p>

            <div class="border rounded dark:border-gray-800">
              <div v-for="(reservation, index) in customerCourse.previousReservations" :key="index" class="flex items-center justify-between border-b dark:border-gray-800 last:border-none p-3">
                <div>
                  <p class="text-sm text-gray-700 dark:text-gray-200">
                    {{ reservation.reservation.dateLabel }}
                  </p>
                  <p class="text-xs text-gray-600 dark:text-gray-300">
                    {{ reservation.reservation._servicesLabel }}
                  </p>
                </div>

                <div class="flex items-center space-x-2">
                  <template v-if="reservation.reservationCustomer">
                    <o-chip v-if="reservation.reservationCustomer.makeupDetails?.isMakeup" variant="info" flat>
                      {{ $t('pages.customer.course.id.makeup') }}
                    </o-chip>

                    <o-chip v-if="reservation.reservationCustomer.checkedIn" variant="success" flat>
                      {{ $t('reservation.checkedIn') }}
                    </o-chip>
                    <o-chip v-else-if="reservation.reservationCustomer.noShow" variant="danger" flat>
                      {{ $t('reservation.noShow') }}
                    </o-chip>

                    <o-panel-link
                      tag="o-button"
                      size="sm"
                      flat
                      icon="view"
                      :path="`/appointment/attendee/${reservation.reservationCustomer.idReservationCustomer}`"
                    />
                  </template>
                </div>
              </div>
            </div>
          </div>
        </dl>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="customerCourse" class="flex justify-between">
        <div>
          <o-button flat outline @click="cancel()">
            {{ $t('general.cancel') }}
          </o-button>
        </div>

        <div>
          <o-button variant="primary" icon="save" outline @click="save()">
            {{ $t('general.save') }}
          </o-button>

          <o-button variant="primary" icon="save" @click="save(true)">
            {{ $t('general.saveAndClose') }}
          </o-button>
        </div>
      </div>
    </template>

    <delete-dialog
      v-if="customerCourse"
      ref="deleteDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="cancel(true)"
    />

    <cancel-dialog
      v-if="customerCourse"
      ref="cancelDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="cancel(true)"
    />

    <book-makeup-dialog
      v-if="customerCourse"
      ref="bookMakeupDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="handleBookMakeupSuccess"
    />

    <move-dialog
      v-if="customerCourse"
      ref="moveDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="handleMoveSuccess"
    />

    <create-order-dialog
      v-if="customerCourse"
      ref="createOrderDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="handleCreateOrderSuccess"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import CustomerSelect from '@/components/customer/elements/Select.vue'
// @ts-ignore
import CreateOrderDialog from '@/components/order/actions/CreateOrder.vue'
// @ts-ignore
import DeleteDialog from '@/components/customerCourse/actions/Delete.vue'
// @ts-ignore
import CancelDialog from '@/components/customerCourse/actions/Cancel.vue'
// @ts-ignore
import BookMakeupDialog from '@/components/customerCourse/actions/BookMakeup.vue'
// @ts-ignore
import MoveDialog from '@/components/customerCourse/actions/Move.vue'

// @ts-ignore
import {
  OTable,
  ODescriptionDetails,
} from '@/components/elements'

// @ts-ignore
import {
  TextField,
  CurrencyField,
  DateField,
  TextAreaField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'
import { CustomerCourseStatus } from '@/_api/models/course'


import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
}>()

const { t } = useI18n()
const { $api, $auth, $bus, $notification } = useNuxtApp()
const API = { CustomerCourseStatus }

const panel = usePanel()

const deleteDialog = ref<any>()
const cancelDialog = ref<any>()
const bookMakeupDialog = ref<any>()
const moveDialog = ref<any>()
const createOrderDialog = ref<any>()
const confirmDialog = ref<any>()

const customerCourse = ref<any>(null)
const loading = ref(true)

const { unsavedChanges, snapshot } = useUnsavedChanges(customerCourse)

const location = computed(() => $auth.info?.location)
const orderChipVariant = computed(() => {
  if (!customerCourse.value?.order) {
    return 'info'
  }

  const status = customerCourse.value.order.statusLabel
  switch (status) {
    case 'Open':
      return 'warning'
    case 'Closed':
      return 'success'
    default:
      return 'info'
  }
})

const locationTime = computed(() => {
  const date = new Date()

  const localTime = date.getTime()
  const localOffset = date.getTimezoneOffset() * 60000

  const utc = localTime + localOffset

  return utc
})

const fetchCustomerCourse = async (idCustomerCourse?: string) => {
  loading.value = true

  try {
    if (idCustomerCourse) {
      const response = await $api.customerCourse.get(idCustomerCourse)
      customerCourse.value = response.data
      snapshot()
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const save = async (close = false) => {
  loading.value = true

  try {
    if (customerCourse.value?.customer?.isNewRecord) {
      const customer = await saveCustomer()

      if (!customer) {
        loading.value = false
        emit('update:loading', false)
        return
      }
    }

    const saveResponse = await $api.customerCourse.save(customerCourse.value)
    const result = saveResponse.data

    if (result) {
      snapshot()

      $notification.toast({
        title: t('pages.customer.course.id.saveSuccessTitle'),
        text: t('pages.customer.course.id.saveSuccessText'),
        variant: 'success',
      })

      $bus.emit.updated('CustomerCourse', result)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    return false
  }

  loading.value = false
}

async function saveCustomer () {
  try {
    const response = await $api.customer.save(customerCourse.value.customer)
    const savedCustomer = response.data

    if (savedCustomer) {
      customerCourse.value.customer = savedCustomer

      $notification.toast({
        title: t('customer.createdSuccess'),
        text: t('customer.createdSuccessInfo', { name: savedCustomer.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', savedCustomer)

      return savedCustomer
    }
  } catch (e) {
    $notification.toast({
      title: t('general.error'),
      text: t('customer.createError'),
      variant: 'danger',
    })
  }

  return null
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleDelete = () => {
  deleteDialog.value.open(customerCourse.value)
}

const handleCancel = () => {
  cancelDialog.value.open(customerCourse.value)
}

const handleBookMakeup = (idReservationCustomer: number | string) => {
  bookMakeupDialog.value.open(customerCourse.value, idReservationCustomer)
}

const handleBookMakeupSuccess = (updatedCustomerCourse: any) => {
  customerCourse.value = updatedCustomerCourse
  snapshot()
}

const handleMove = () => {
  moveDialog.value.open(customerCourse.value)
}

const handleMoveSuccess = (updatedCustomerCourse: any) => {
  customerCourse.value = updatedCustomerCourse
  snapshot()
}

const handleCreateOrder = () => {
  const customer = customerCourse.value.customer
  const date = new Date()
  const items = [customerCourse.value]

  const data = {
    customer,
    date,
    items
  }

  createOrderDialog.value.open(data)
}

const handleCreateOrderSuccess = () => {
  fetchCustomerCourse(props.id)
}

watch(() => props.id, (id) => fetchCustomerCourse(id), { immediate: true })
</script>
